<template>
  <TheModal :is-open="isOpen" modal-title="Manage account credit" max-width="max-w-sm" @close-modal="onClose">
    <template #main>
      <Form v-slot="{ errors }" @submit.prevent="saveCreditModal()">
        <div>
          <SegmentedRadio v-model="creditAction" class="mb-4" label="Action" :options="CREDIT_ACTIONS" />
          <FormLabel for="accountCredit" label="Credit amount" />
          <div class="flex rounded-md shadow-sm">
            <span
              class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm"
            >
              $
            </span>
            <Field
              id="accountCredit"
              v-model="accountCreditAmount"
              :rules="fieldIsRequired"
              oninput="this.value = this.value.replace(/[^0-9\.]/g,'')"
              type="text"
              name="accountCredit"
              inputmode="decimal"
              placeholder="0.00"
              autocomplete="off"
              class="flex-1 block w-full focus:ring-blue-500 focus:border-blue-500 min-w-0 rounded-none rounded-r-md sm:text-sm border-gray-300"
            />
          </div>
          <InputError v-show="errors.accountCredit" />
          <div v-if="creditPreviewText" class="mt-3 text-sm text-gray-600">
            {{ creditPreviewText }}
          </div>
          <div v-if="onSubmitMessage" class="mt-4 text-red-600">
            <div class="text-sm font-medium">
              {{ onSubmitMessage }}
            </div>
            <div v-if="onSubmitLog" class="mt-1 text-sm">
              {{ onSubmitLog }}
            </div>
          </div>
        </div>
      </Form>
    </template>
    <template #footer>
      <button class="btn-ghost justify-center" type="button" @click="onClose">Cancel</button>
      <button class="btn-primary justify-center" type="button" @click="saveCreditModal()">Apply</button>
    </template>
  </TheModal>
</template>
<script>
import { Field, Form } from 'vee-validate'
import { computed, defineComponent, ref, watch } from 'vue'

import FormLabel from '@/components/forms/FormLabel.vue'
import InputError from '@/components/forms/InputError.vue'
import SegmentedRadio from '@/components/forms/SegmentedRadio.vue'
import TheModal from '@/components/TheModal.vue'
import api from '@/store/api'

const CREDIT_ACTIONS = [
  { label: '+ Add credit', value: 'add' },
  { label: '− Remove credit', value: 'remove' }
]

export default defineComponent({
  name: 'ManageAccountCreditModal',
  components: { TheModal, Form, Field, FormLabel, InputError, SegmentedRadio },
  props: {
    isOpen: { type: Boolean, default: false },
    conversationUserPairId: { type: String, required: true }
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    const creditAction = ref('add')
    const accountCreditAmount = ref('')
    const onSubmitMessage = ref('')
    const onSubmitLog = ref('')

    watch(
      () => props.isOpen,
      open => {
        if (open) {
          creditAction.value = 'add'
          accountCreditAmount.value = ''
          onSubmitMessage.value = ''
          onSubmitLog.value = ''
        }
      }
    )

    const creditPreviewText = computed(() => {
      const amount = parseFloat(accountCreditAmount.value)
      if (!amount || isNaN(amount) || amount <= 0) return ''
      const formattedAmount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
      if (creditAction.value === 'add') {
        return `Account credit will increase by ${formattedAmount}`
      } else {
        return `Account credit will decrease by ${formattedAmount}`
      }
    })

    const fieldIsRequired = val => {
      return val ? true : 'This field is required'
    }

    function onClose() {
      emit('close')
    }

    function saveCreditModal() {
      const amount = parseFloat(accountCreditAmount.value)
      if (!amount || isNaN(amount) || amount <= 0) {
        onSubmitMessage.value = 'Please enter a valid amount'
        return
      }
      const isRemove = creditAction.value === 'remove'
      const accountCreditAmountCents = Math.round(amount * 100) * (isRemove ? -1 : 1)
      api
        .call(
          'POST',
          `conversation_user_pairs/${props.conversationUserPairId}/account_credits`,
          {
            account_credit: {
              value: accountCreditAmountCents
            }
          },
          false
        )
        .then(response => {
          const attrs = response.data.data.attributes
          emit('success', attrs?.account_credit?.format ?? null)
          emit('close')
        })
        .catch(error => {
          onSubmitMessage.value = 'Something went wrong!'
          onSubmitLog.value = error
        })
    }

    return {
      CREDIT_ACTIONS,
      creditAction,
      accountCreditAmount,
      onSubmitMessage,
      onSubmitLog,
      creditPreviewText,
      fieldIsRequired,
      onClose,
      saveCreditModal
    }
  }
})
</script>
